import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, LinearProgress, Skeleton, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import LoadingSkeleton from './LoadingSkeleton';

const GROUPS = [
  { key: 'childCount', labelKey: 'child', color: '#ff9800' },
  { key: 'adultCount', labelKey: 'adult', color: '#4caf50' },
  { key: 'elderCount', labelKey: 'elder', color: '#9c27b0' },
];

const AgeSkeletonPlaceholder = () => (
  <Box mt={1.5} sx={{ width: '100%' }}>
    {[1, 2, 3].map((i) => (
      <Box key={i} mb={1.25}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Skeleton variant="text" width={60} height={16} animation="wave" />
          <Skeleton variant="text" width={40} height={16} animation="wave" />
        </Box>
        <Skeleton variant="rounded" width="100%" height={6} animation="wave" sx={{ borderRadius: 3 }} />
      </Box>
    ))}
    <Skeleton variant="text" width={80} height={16} animation="wave" sx={{ mt: 0.5 }} />
  </Box>
);

const AgeCard = ({ ageSummary = null, sx = {}, loading = false }) => {
  const { t } = useTranslation();

  const total = ageSummary?.total ?? 0;
  const hasData = total > 0;

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        ...sx,
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 16 }}>
            <GroupsIcon fontSize="inherit" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('dashboard:kpi.ageDistribution')}
          </Typography>
        </Box>

        <LoadingSkeleton loading={loading} height={105} customSkeleton={<AgeSkeletonPlaceholder />}>
          {!hasData ? (
            <Box mt={1.5} textAlign="center" py={1}>
              <Typography variant="body2" color="text.secondary">
                {t('common:labels.noData')}
              </Typography>
            </Box>
          ) : (
            <Box mt={1.5}>
              {GROUPS.map(g => {
                const count = ageSummary?.[g.key] ?? 0;
                const ratio = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <Box key={g.key} mb={1.25}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t(`dashboard:kpi.${g.labelKey}`)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {count.toLocaleString('pt-PT')}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: g.color, minWidth: 32, textAlign: 'right' }}
                        >
                          {ratio}%
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={ratio}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': { bgcolor: g.color, borderRadius: 3 },
                      }}
                    />
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {total.toLocaleString('pt-PT')} {t('dashboard:kpi.classified')}
              </Typography>
            </Box>
          )}
        </LoadingSkeleton>
      </CardContent>
    </Card>
  );
};

AgeCard.propTypes = {
  ageSummary: PropTypes.shape({
    childCount: PropTypes.number,
    adultCount: PropTypes.number,
    elderCount: PropTypes.number,
    total: PropTypes.number,
  }),
  sx: PropTypes.object,
  loading: PropTypes.bool,
};

export default AgeCard;
